import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Scale } from 'lucide-react';
import type { TaxRuleToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { SystemGrid, type SystemGridColumn } from '~/components/system/grid/system-grid';
import { serverMessage } from '~/lib/api/server-error';
import { useTaxRulesList, useTaxRuleStatuses, useTaxRuleCountries, useTaxRuleRegions, useTaxRuleMutations } from '~/hooks/use-tax-rules';
import { tokenToForm, type TaxRuleFormValues } from '~/lib/schemas/tax-rule';
import { TaxRuleEditDialog } from './tax-rule-edit-dialog';

/**
 * Tax rules (Φορολογικοί Κανόνες) CRUD surface. Built against the live TaxRuleToken contract
 * (country / region / postal code → tax status); the legacy admin grid's category/rate columns
 * are obsolete server-side and intentionally not reproduced.
 */
export function TaxRulesGridView() {
	const { t } = useTranslation();
	const { data, isLoading, isError, refetch } = useTaxRulesList();
	const { data: statuses = [] } = useTaxRuleStatuses();
	const { data: countries = [] } = useTaxRuleCountries();
	const { data: regions = [] } = useTaxRuleRegions();
	const { remove } = useTaxRuleMutations();

	const [editOpen, setEditOpen] = useState(false);
	const [editing, setEditing] = useState<TaxRuleFormValues | null>(null);
	const [deleteRow, setDeleteRow] = useState<TaxRuleToken | null>(null);

	const statusMap = useMemo(() => new Map(statuses.map(o => [o.id, o.nm])), [statuses]);
	const countryMap = useMemo(() => new Map(countries.map(c => [c.id, c.nm])), [countries]);
	const regionMap = useMemo(() => new Map(regions.map(r => [r.id, r.nm])), [regions]);
	const countryOf = (o: TaxRuleToken) => (o.country_id == null ? '' : (countryMap.get(o.country_id) ?? ''));
	const regionOf = (o: TaxRuleToken) => (o.country_region_id == null ? '' : (regionMap.get(o.country_region_id) ?? ''));
	const statusOf = (o: TaxRuleToken) => (o.tax_status == null ? '' : (statusMap.get(o.tax_status) ?? ''));

	const columns = useMemo<SystemGridColumn<TaxRuleToken>[]>(
		() => [
			{
				key: 'country',
				header: t('prices.taxRules.columns.country'),
				width: 'minmax(0,1fr)',
				sortValue: o => countryOf(o),
				exportValue: o => countryOf(o),
				render: o => (
					<span className="truncate text-sm font-medium text-foreground" title={countryOf(o)}>
						{countryOf(o) || '—'}
					</span>
				)
			},
			{
				key: 'region',
				header: t('prices.taxRules.columns.region'),
				width: '200px',
				sortValue: o => regionOf(o),
				exportValue: o => regionOf(o),
				render: o => <span className="truncate text-sm text-foreground-muted">{regionOf(o) || '—'}</span>
			},
			{
				key: 'postalCode',
				header: t('prices.taxRules.columns.postalCode'),
				width: '140px',
				sortValue: o => o.postal_code ?? '',
				exportValue: o => o.postal_code ?? '',
				render: o => <span className="font-mono text-xs text-foreground-muted">{o.postal_code || '—'}</span>
			},
			{
				key: 'taxStatus',
				header: t('prices.taxRules.columns.taxStatus'),
				width: '160px',
				sortValue: o => statusOf(o),
				exportValue: o => statusOf(o),
				render: o => <span className="text-sm text-foreground-muted">{statusOf(o) || '—'}</span>
			}
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[t, statusMap, countryMap, regionMap]
	);

	const confirmDelete = () => {
		if (!deleteRow?.id) return;
		remove.mutate(deleteRow.id, {
			onSuccess: () => {
				toast.success(t('common.delete'));
				setDeleteRow(null);
			},
			onError: err => toast.error(serverMessage(err))
		});
	};

	return (
		<>
			<SystemGrid<TaxRuleToken>
				gridId="tax-rules"
				title={t('prices.taxRules.title')}
				rows={data ?? []}
				loading={isLoading}
				error={isError}
				onRetry={() => void refetch()}
				getRowId={o => o.id ?? `${o.country_id}-${o.country_region_id}-${o.postal_code}`}
				columns={columns}
				searchPredicate={(o, q) => `${countryOf(o)} ${regionOf(o)} ${o.postal_code ?? ''}`.toLowerCase().includes(q)}
				emptyIcon={<Scale size={36} className="text-foreground-disabled" strokeWidth={1.25} />}
				addLabel={t('prices.taxRules.add')}
				onAdd={() => {
					setEditing(null);
					setEditOpen(true);
				}}
				onEdit={o => {
					setEditing(tokenToForm(o));
					setEditOpen(true);
				}}
				onDelete={setDeleteRow}
				exportFilename="tax-rules"
			/>

			<TaxRuleEditDialog
				open={editOpen}
				onOpenChange={setEditOpen}
				initial={editing}
				statuses={statuses}
				countries={countries}
				regions={regions}
			/>

			<Dialog open={Boolean(deleteRow)} onOpenChange={open => !open && setDeleteRow(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('prices.taxRules.deleteTitle')}</DialogTitle>
						<DialogDescription>
							{t('prices.taxRules.deleteBody', { name: countryOf(deleteRow ?? ({} as TaxRuleToken)) || '' })}
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setDeleteRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button variant="destructive" onClick={confirmDelete} disabled={remove.isPending}>
							{t('common.delete')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
